/**
 * Stage 7: stock inventories -- the shapes, the vocabulary, and the month-end
 * billing CSV.
 *
 * A stock inventory is the same inventory everything else already is (items,
 * photos, fields, search, sharing), with paperwork around stock coming in and
 * going out: a **receipt** brings items in against a vendor invoice, a
 * **deployment** sends them to a **client**, a return brings them back as the
 * same item, and a **retirement** takes one out of service for good without
 * deleting it. Every one of those writes a **movement**, which is the item's
 * history.
 *
 * **Cost and the vendor's invoice number are private.** They live in the
 * stock tables, never in an item's fields, so `publicFields()` -- the only
 * thing the Stage 3 prompt and the Stage 4 listing read -- cannot see them to
 * begin with. The billing export below is allowed to carry them, because it
 * is the company's own books, not a stranger or a model; see
 * docs/notes/invariants.md.
 */

import type { ItemHold, Reserve, ReserveHold, RowHold } from "./reserves.js";

/** Chosen when an inventory is made, and never changed: the stock workflow never appears in a personal one. */
export type InventoryKind = "personal" | "stock";

export const INVENTORY_KINDS: readonly InventoryKind[] = ["personal", "stock"];

export const INVENTORY_KIND_LABELS: Record<InventoryKind, string> = {
  personal: "Personal",
  stock: "Stock",
};

/** How many inventories one account may own. A typo guard and a cost ceiling, not a price tier. */
export const MAX_OWNED_INVENTORIES = 10;

/**
 * One laptop per item, tracked by its serial -- or one line with a count, where
 * shipping 3 of 20 power supplies leaves 17.
 */
export type StockTracking = "serialized" | "bulk";

export const STOCK_TRACKINGS: readonly StockTracking[] = ["serialized", "bulk"];

export const TRACKING_LABELS: Record<StockTracking, string> = {
  serialized: "Serialized",
  bulk: "Bulk",
};

/**
 * Where a stock item is. A bulk line stays `in_stock` for as long as it is a
 * line at all -- its units come and go through `onHand` and its deployments,
 * not through this.
 */
export type StockState = "in_stock" | "deployed" | "retired";

export const STOCK_STATE_LABELS: Record<StockState, string> = {
  in_stock: "In stock",
  deployed: "Deployed",
  retired: "Retired",
};

/**
 * Why something left service. The roadmap's list, plus "entered by mistake"
 * (owner, 2026-10-05): a unit received or typed in that was never really
 * there. That one frees a serialized unit's serial and cancels whatever it
 * had waiting to bill, and is refused once anything about it was billed.
 */
export type RetireReason =
  | "end_of_life"
  | "broken"
  | "lost_or_stolen"
  | "returned_to_vendor"
  | "sold"
  | "recycled"
  | "entered_by_mistake";

export const RETIRE_REASONS: readonly RetireReason[] = [
  "end_of_life",
  "broken",
  "lost_or_stolen",
  "returned_to_vendor",
  "sold",
  "recycled",
  "entered_by_mistake",
];

export const RETIRE_REASON_LABELS: Record<RetireReason, string> = {
  end_of_life: "End of life",
  broken: "Broken",
  lost_or_stolen: "Lost or stolen",
  returned_to_vendor: "Returned to the vendor",
  sold: "Sold",
  recycled: "Recycled",
  entered_by_mistake: "Entered by mistake",
};

/**
 * What a voided unit's serial becomes: "PF1ABC (void)". Kept rather than
 * cleared, so the history still says which serial was typed, but no longer
 * equal to the real one, so the right unit can be received under it.
 */
export const VOID_SERIAL_SUFFIX = " (void)";

export type MovementKind =
  "received" | "deployed" | "returned" | "retired" | "reserved" | "released" | "converted";

export const MOVEMENT_LABELS: Record<MovementKind, string> = {
  received: "Received",
  deployed: "Deployed",
  returned: "Returned",
  retired: "Retired",
  reserved: "Held in reserve",
  released: "Released from reserve",
  converted: "Converted",
};

/** The field label a serialized item's serial is kept under, so search finds it like any other field. */
export const SERIAL_FIELD_LABEL = "Serial number";

/* ------------------------------------------------------------- records -- */

/**
 * The colours a client can wear, by name. Each is a pair of theme tokens
 * (`--client-<name>` and `--client-<name>-soft`), so it holds up in dark mode
 * and is checked for contrast like every other colour in the app.
 */
export const CLIENT_COLORS = ["red", "orange", "amber", "green", "teal", "blue", "violet", "pink"] as const;
export type ClientColor = (typeof CLIENT_COLORS)[number];

export const CLIENT_COLOR_LABELS: Record<ClientColor, string> = {
  red: "Red",
  orange: "Orange",
  amber: "Amber",
  green: "Green",
  teal: "Teal",
  blue: "Blue",
  violet: "Violet",
  pink: "Pink",
};

export const normaliseClientColor = (value: unknown): ClientColor | null =>
  (CLIENT_COLORS as readonly unknown[]).includes(value) ? (value as ClientColor) : null;

/** A client company or site. Its own list, separate from the location tree: a shelf is not a customer. */
export interface Client {
  id: string;
  name: string;
  /** Null is the plain grey every client starts with. */
  color: ClientColor | null;
  createdAt: number;
  /** Units at this client right now, across every open deployment. */
  deployedUnits: number;
}

/** Long enough for "Remote, Jane Smith, home office"; short enough to stay one line on a phone. */
export const MAX_SPOT_LENGTH = 80;

/** "Branch A" or "Branch A · Desk 14": where a deployment is, as one line. */
export const atClient = (clientName: string, spot: string): string =>
  spot ? `${clientName} · ${spot}` : clientName;

/**
 * A file kept against a receipt (the vendor's invoice) or a retirement (a wipe
 * or shred certificate). Like `PhotoMeta`, the R2 key never leaves the worker.
 */
export interface AttachmentMeta {
  id: string;
  name: string;
  contentType: string;
  bytes: number;
  createdAt: number;
}

export interface Receipt {
  id: string;
  vendor: string;
  /** Private: see the file comment. */
  invoiceNumber: string;
  /** `YYYY-MM-DD`, the day it arrived -- a calendar date, not a moment. */
  receivedOn: string;
  notes: string;
  attachment: AttachmentMeta | null;
  itemCount: number;
  /** What the receipt cost in total, from the unit costs on its lines. Private. */
  totalCostCents: number;
  createdAt: number;
  actor: string;
}

/** One item's stock facts, as the stock list and the item screen show them. */
export interface StockInfo {
  itemId: string;
  tracking: StockTracking;
  state: StockState;
  /** Units on the shelf. Always 0 or 1 for a serialized item. */
  onHand: number;
  /** Units out at clients right now, across every open deployment. */
  deployed: number;
  /** Private. Integer cents, like every other amount in this app. */
  unitCostCents: number | null;
  receiptId: string | null;
  /** Where a serialized item is right now; null unless it is deployed. */
  clientId: string | null;
}

/** A row of the stock list: enough of the item to recognise it, plus its stock facts. */
export interface StockRow extends StockInfo {
  title: string;
  serial: string;
  categoryId: string | null;
  coverPhotoId: string | null;
  /** The item's own price, as the item screen has it. Null when nobody has priced it. */
  priceCents: number | null;
  /** Who its shelf units are held for, one entry per reserve. Empty when nothing is held. */
  holds: RowHold[];
}

/**
 * Stock sent to a client. Its cost and bill-out price are frozen at the
 * moment it went out, and `billable` is the person's call every time -- the
 * owner's answer to "is a redeployed laptop billed again" was "leave it to
 * whoever is deploying it".
 */
export interface Deployment {
  id: string;
  itemId: string;
  clientId: string;
  quantity: number;
  /** How many of `quantity` have come back. */
  returned: number;
  /** Private. */
  unitCostCents: number | null;
  unitPriceCents: number;
  /**
   * What the carrier charged to send this line, in total. Billed to the client
   * at cost as a line of its own, next to the item's, never folded into the
   * price each.
   */
  shippingCents: number;
  billable: boolean;
  /** When it went into a month-end export and was marked billed; null while it is still waiting. */
  billedAt: number | null;
  deployedOn: string;
  /**
   * Where at the client it is, in the stockroom's own words: "Desk 14",
   * "Remote, J. Smith". Empty when nobody said. Not a location: the client is
   * where it is, and this is only the note beside it.
   */
  spot: string;
  createdAt: number;
  actor: string;
}

/** An open deployment, as the stock list's "Deployed" pile shows it. */
export interface OpenDeployment extends Deployment {
  title: string;
  serial: string;
  clientName: string;
  tracking: StockTracking;
}

export interface Retirement {
  id: string;
  itemId: string;
  quantity: number;
  reason: RetireReason;
  retiredOn: string;
  disposalVendor: string;
  note: string;
  certificate: AttachmentMeta | null;
  createdAt: number;
  actor: string;
}

export interface RetirementRow extends Retirement {
  title: string;
  serial: string;
}

/** One line of an item's history. */
export interface Movement {
  id: string;
  itemId: string;
  kind: MovementKind;
  quantity: number;
  clientId: string | null;
  clientName: string;
  receiptId: string | null;
  deploymentId: string | null;
  retirementId: string | null;
  /** The bill-out price, on a deployment. */
  priceCents: number | null;
  /** Private: the unit cost at the time. */
  costCents: number | null;
  note: string;
  /** Who did it, as the email they were signed in with at the time. A record, not a reference. */
  actor: string;
  /**
   * The calendar day it happened, as the person gave it: the receipt's
   * arrival, the day it went out, came back or was retired. What the history
   * is ordered and read by -- a laptop deployed on the 8th and entered on the
   * 23rd went out on the 8th.
   */
  occurredOn: string;
  /** When the row was written. */
  at: number;
}

/** Everything the item screen's stock panel shows. */
export interface StockDetail {
  stock: StockInfo;
  receipt: Receipt | null;
  deployments: Deployment[];
  retirements: Retirement[];
  movements: Movement[];
  /** What it is held for right now. */
  holds: ItemHold[];
  /** Client reserves whose tags this item wears: what it could be pulled into. */
  reserves: Reserve[];
}

export interface StockTotals {
  /** Units on the shelf. */
  onHand: number;
  deployed: number;
  retired: number;
  /** What is on the shelf cost. Private. */
  onHandCostCents: number;
  /** Units deployed and billable that nobody has marked billed yet. */
  unbilledLines: number;
}

export interface StockOverview {
  items: StockRow[];
  open: OpenDeployment[];
  retired: RetirementRow[];
  totals: StockTotals;
  /** What the Stock screen's filter chips read, for every item in stock (retired too), by item id. */
  facets: Record<string, StockFacets>;
}

/**
 * The parts of an item the Stock screen filters on, beside what each row
 * already carries. One map rather than more columns on three row types: the
 * deployed and retired rows are a deployment and a retirement, not an item.
 */
export interface StockFacets {
  categoryId: string | null;
  locationId: string | null;
  /** Tag ids. */
  tagIds: string[];
  tracking: StockTracking;
}

/**
 * The Stock screen's chips, as picked. Undefined is "any". `category` and
 * `location` take "none" for "not set"; a location matches what is filed
 * anywhere below it, as it does on the Items screen.
 */
export interface StockFilters {
  category?: string | undefined;
  location?: string | undefined;
  tag?: string | undefined;
  kind?: StockTracking | undefined;
}

/**
 * Whether an item's facets pass the chips. `parentOf` walks the location tree
 * up, so a shelf matches the bins on it; it is a function because the tree
 * lives in the screen's taxonomy, not in the overview.
 */
export function matchesStockFilters(
  facets: StockFacets | undefined,
  filters: StockFilters,
  parentOf: (locationId: string) => string | null,
): boolean {
  if (!facets) return Object.values(filters).every((value) => value === undefined);
  if (filters.kind && facets.tracking !== filters.kind) return false;
  if (filters.tag && !facets.tagIds.includes(filters.tag)) return false;
  if (filters.category !== undefined) {
    if (filters.category === "none" ? facets.categoryId !== null : facets.categoryId !== filters.category) {
      return false;
    }
  }
  if (filters.location !== undefined) {
    if (filters.location === "none") return facets.locationId === null;
    // Up from where it is filed; a cycle (which the tree refuses anyway) stops at the guard.
    let at = facets.locationId;
    for (let steps = 0; at !== null && steps < 64; steps += 1) {
      if (at === filters.location) return true;
      at = parentOf(at);
    }
    return false;
  }
  return true;
}

/**
 * Everything a stockroom has recorded, whole: the Stage 5 export's stock half.
 * Unlike the overview, retired stock and closed deployments are in it, and
 * every receipt rather than the latest hundred.
 */
export interface StockRecords {
  stock: StockRow[];
  receipts: Receipt[];
  clients: Client[];
  deployments: Deployment[];
  retirements: Retirement[];
  movements: Movement[];
  reserves: Reserve[];
  /** Every hold ever made, released ones included. */
  holds: ReserveHold[];
}

export interface StockSettings {
  /** The standard markup a deployment's price starts from. Whole percent, 0 to 1000. */
  markupPercent: number;
}

/** A line of the month-end export: one deployment, deployed and billable, and not yet billed. */
export interface BillingLine {
  /**
   * A deployment's shipping is a second line, sharing its deployment's id:
   * billing one bills both. A `reserve` line is stock held for a client who
   * pays in advance, and `deploymentId` is then the hold's id.
   */
  kind: "item" | "shipping" | "reserve";
  deploymentId: string;
  deployedOn: string;
  clientId: string;
  clientName: string;
  itemId: string;
  title: string;
  description: string;
  serial: string;
  categoryName: string;
  /** What is billed: deployed, less anything that came back before it was billed. */
  quantity: number;
  unitPriceCents: number;
  unitCostCents: number | null;
  vendor: string;
  vendorInvoice: string;
}

/* ------------------------------------------------------------- writes -- */

export interface ReceiptLineInput {
  title: string;
  tracking: StockTracking;
  quantity: number;
  unitCostCents: number | null;
  categoryId?: string | null;
  locationId?: string | null;
  /** Serialized only: one per unit, in order. Fewer than `quantity` leaves the rest blank to fill in later. */
  serials?: string[];
  /** Bulk only: add to a bulk line already in stock rather than starting a new one. */
  existingItemId?: string | null;
  /**
   * Tags every unit on this line wears -- reserve tags, picked on the
   * receiving screen, so stock is matched to reserves as it arrives. A bulk
   * top-up adds them to the line it tops up.
   */
  tags?: string[];
}

export interface ReceiptInput {
  vendor: string;
  invoiceNumber: string;
  receivedOn: string;
  notes: string;
  lines: ReceiptLineInput[];
}

/**
 * An item that is already in the inventory -- captured on the Items screen,
 * not received against an invoice -- going onto the shelf. It gets the same
 * "received" line in its history as anything off a receipt, with no receipt.
 */
export interface ShelveInput {
  tracking: StockTracking;
  /** Always 1 for serialized. */
  quantity: number;
  unitCostCents: number | null;
  /** `YYYY-MM-DD`, the day it came into stock. */
  receivedOn: string;
}

export interface DeployLineInput {
  itemId: string;
  quantity: number;
  unitPriceCents: number;
  /** Total shipping for the line, at cost. Absent means none. */
  shippingCents?: number;
  billable: boolean;
  /**
   * Units held for another client's reserve go out only when this says so --
   * the "Branch B needs it sooner" case, said out loud. That reserve goes
   * short, and its alert says so.
   */
  takeReserved?: boolean;
}

export interface DeployInput {
  clientId: string;
  deployedOn: string;
  lines: DeployLineInput[];
  /** Where at the client, for every line. Each line's can be changed after. */
  spot?: string;
}

export interface ReturnInput {
  deploymentId: string;
  quantity: number;
  /** `YYYY-MM-DD`, the day it came back. */
  returnedOn: string;
}

/**
 * One deployment line going straight to another client: it comes back to the
 * shelf and goes out again in the same write, so the history says both and
 * billing treats it as a return and a new deployment -- which is what it is.
 */
export interface TransferLineInput {
  deploymentId: string;
  quantity: number;
  unitPriceCents: number;
  shippingCents?: number;
  billable: boolean;
}

export interface TransferInput {
  clientId: string;
  /** `YYYY-MM-DD`: the day it came back from one client and went to the other. */
  movedOn: string;
  lines: TransferLineInput[];
  /** Where at the new client. The old client's spot does not follow it. */
  spot?: string;
}

export interface RetireInput {
  itemId: string;
  quantity: number;
  reason: RetireReason;
  retiredOn: string;
  disposalVendor: string;
  note: string;
}

/** Caps, like the item text caps: bounds on a typo or a loop, not rules about stockrooms. */
export const MAX_RECEIPT_LINES = 100;
export const MAX_RECEIPT_UNITS = 500;
export const MAX_DEPLOY_LINES = 200;
export const MAX_MARKUP_PERCENT = 1000;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_TYPES: readonly string[] = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
];
export const ATTACHMENT_PART = "file";

/* ------------------------------------------------------------ helpers -- */

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A real calendar date as `YYYY-MM-DD`, or null. "2026-02-30" is not one. */
export function validDate(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const match = DATE.exec(value.trim());
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  return date.toISOString().slice(0, 10) === value.trim() ? value.trim() : null;
}

/** Today, as the person's own calendar has it. Used for defaults only; the server never guesses a date. */
export const todayLocal = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

/**
 * Cost plus the standard markup, rounded to the cent. The deploy form starts
 * here and the person types over it when this client pays something else --
 * the owner's "markup, with an override per line".
 */
export function priceWithMarkup(costCents: number | null, markupPercent: number): number | null {
  if (costCents === null) return null;
  return Math.round((costCents * (100 + markupPercent)) / 100);
}

export function normaliseMarkup(value: unknown): number {
  const percent = Math.round(Number(value));
  return Number.isFinite(percent) ? Math.min(Math.max(percent, 0), MAX_MARKUP_PERCENT) : 0;
}

/* ---------------------------------------------------- the billing CSV -- */

/**
 * What a column of the export can be filled from. `unit_cost`, `line_cost`,
 * `vendor` and `vendor_invoice` are the private ones; the template editor
 * marks them, and no preset uses them.
 */
export type ExportSource =
  | "deployed_on"
  | "client"
  | "title"
  | "description"
  | "serial"
  | "category"
  | "quantity"
  | "unit_price"
  | "line_total"
  | "unit_cost"
  | "line_cost"
  | "vendor"
  | "vendor_invoice"
  | "invoice_ref"
  | "invoice_date"
  | "due_date"
  | "deployment_id"
  | "constant";

export const EXPORT_SOURCES: readonly ExportSource[] = [
  "deployed_on",
  "client",
  "title",
  "description",
  "serial",
  "category",
  "quantity",
  "unit_price",
  "line_total",
  "unit_cost",
  "line_cost",
  "vendor",
  "vendor_invoice",
  "invoice_ref",
  "invoice_date",
  "due_date",
  "deployment_id",
  "constant",
];

export const EXPORT_SOURCE_LABELS: Record<ExportSource, string> = {
  deployed_on: "Date deployed",
  client: "Client",
  title: "Item title",
  description: "Item description",
  serial: "Serial number",
  category: "Category",
  quantity: "Quantity",
  unit_price: "Unit price",
  line_total: "Line total",
  unit_cost: "Unit cost (private)",
  line_cost: "Line cost (private)",
  vendor: "Vendor (private)",
  vendor_invoice: "Vendor invoice no. (private)",
  invoice_ref: "Invoice number (one per client)",
  invoice_date: "Invoice date (export day)",
  due_date: "Due date",
  deployment_id: "utucat line id",
  constant: "Fixed text",
};

export const PRIVATE_EXPORT_SOURCES: readonly ExportSource[] = [
  "unit_cost",
  "line_cost",
  "vendor",
  "vendor_invoice",
];

export type ExportDateFormat = "iso" | "us" | "uk";

export const DATE_FORMAT_LABELS: Record<ExportDateFormat, string> = {
  iso: "2026-09-30",
  us: "09/30/2026",
  uk: "30/09/2026",
};

export interface ExportColumn {
  header: string;
  source: ExportSource;
  /** Only for `constant`. */
  value?: string;
}

export interface ExportTemplateSpec {
  name: string;
  columns: ExportColumn[];
  dateFormat: ExportDateFormat;
  /** Days from the export to the due date. */
  dueDays: number;
  /** Put before every invoice number, so utucat's numbers never collide with the billing system's own. */
  invoicePrefix: string;
}

export interface ExportTemplate extends ExportTemplateSpec {
  id: string;
  /** A preset shipped with the app: read-only, and copied rather than edited. */
  builtIn: boolean;
  /**
   * Whether the preset was checked against the target system's own import
   * documentation. Only the generic and QuickBooks ones were: ConnectWise and
   * Autotask take charges mostly through their APIs, and neither publishes a
   * CSV import shape this could be checked against.
   */
  verified: boolean;
  /** One line for the screen, about where it came from and what to check. */
  note: string;
  createdAt: number;
}

export const MAX_EXPORT_COLUMNS = 30;
export const MAX_EXPORT_TEMPLATES = 20;

const PRESET = (id: string, spec: ExportTemplateSpec, verified: boolean, note: string): ExportTemplate => ({
  id,
  ...spec,
  builtIn: true,
  verified,
  note,
  createdAt: 0,
});

/**
 * The shipped presets. Their ids are not minted ids (they contain a hyphen), so
 * `validId()` can never confuse one with a custom template's.
 */
export const BUILTIN_TEMPLATES: readonly ExportTemplate[] = [
  PRESET(
    "builtin-generic",
    {
      name: "Generic CSV",
      dateFormat: "iso",
      dueDays: 30,
      invoicePrefix: "",
      columns: [
        { header: "Date", source: "deployed_on" },
        { header: "Client", source: "client" },
        { header: "Item", source: "title" },
        { header: "Serial", source: "serial" },
        { header: "Quantity", source: "quantity" },
        { header: "Unit price", source: "unit_price" },
        { header: "Total", source: "line_total" },
        { header: "Reference", source: "deployment_id" },
      ],
    },
    true,
    "Plain columns for importing by hand, or for a spreadsheet.",
  ),
  PRESET(
    "builtin-quickbooks",
    {
      name: "QuickBooks Online invoices",
      dateFormat: "us",
      dueDays: 30,
      invoicePrefix: "UTU-",
      columns: [
        { header: "Invoice number", source: "invoice_ref" },
        { header: "Customer", source: "client" },
        { header: "Invoice date", source: "invoice_date" },
        { header: "Due date", source: "due_date" },
        { header: "Product/Service", source: "title" },
        { header: "Item description", source: "serial" },
        { header: "Item quantity", source: "quantity" },
        { header: "Item rate", source: "unit_price" },
        { header: "Item amount", source: "line_total" },
        { header: "Service date", source: "deployed_on" },
      ],
    },
    true,
    "One invoice per client per export. QuickBooks takes 100 invoices per import, and customer names have to match what is already in QuickBooks.",
  ),
  PRESET(
    "builtin-connectwise",
    {
      name: "ConnectWise PSA (starting point)",
      dateFormat: "us",
      dueDays: 30,
      invoicePrefix: "",
      columns: [
        { header: "Company", source: "client" },
        { header: "Product ID", source: "title" },
        { header: "Description", source: "description" },
        { header: "Serial Number", source: "serial" },
        { header: "Quantity", source: "quantity" },
        { header: "Price", source: "unit_price" },
        { header: "Extended Price", source: "line_total" },
        { header: "Date", source: "deployed_on" },
      ],
    },
    false,
    "Not checked against a real ConnectWise import. Compare it with your import template, then copy it and fix the columns.",
  ),
  PRESET(
    "builtin-autotask",
    {
      name: "Autotask charges (starting point)",
      dateFormat: "us",
      dueDays: 30,
      invoicePrefix: "",
      columns: [
        { header: "Account Name", source: "client" },
        { header: "Charge Name", source: "title" },
        { header: "Description", source: "serial" },
        { header: "Date Purchased", source: "deployed_on" },
        { header: "Units", source: "quantity" },
        { header: "Unit Price", source: "unit_price" },
        { header: "Extended Price", source: "line_total" },
        { header: "Billable", source: "constant", value: "Yes" },
      ],
    },
    false,
    "Not checked against a real Autotask import. Compare it with your import template, then copy it and fix the columns.",
  ),
];

const clampLine = (value: unknown, max: number): string =>
  String(value ?? "")
    .replace(/[\r\n]+/g, " ")
    .trim()
    .slice(0, max);

/** A template as a write may carry it, clamped; null when it has no name or no columns. */
export function normaliseTemplateSpec(value: unknown): ExportTemplateSpec | null {
  if (!value || typeof value !== "object") return null;
  const input = value as Record<string, unknown>;
  const name = clampLine(input["name"], 80);
  if (!name) return null;

  const columns = (Array.isArray(input["columns"]) ? input["columns"] : [])
    .slice(0, MAX_EXPORT_COLUMNS)
    .map((raw): ExportColumn | null => {
      if (!raw || typeof raw !== "object") return null;
      const column = raw as Record<string, unknown>;
      const source = column["source"];
      if (typeof source !== "string" || !(EXPORT_SOURCES as readonly string[]).includes(source)) return null;
      const header = clampLine(column["header"], 80);
      if (!header) return null;
      return source === "constant"
        ? { header, source, value: clampLine(column["value"], 200) }
        : { header, source: source as ExportSource };
    })
    .filter((column): column is ExportColumn => column !== null);
  if (columns.length === 0) return null;

  const format = input["dateFormat"];
  const dueDays = Math.trunc(Number(input["dueDays"]));
  return {
    name,
    columns,
    dateFormat: format === "us" || format === "uk" ? format : "iso",
    dueDays: Number.isFinite(dueDays) ? Math.min(Math.max(dueDays, 0), 365) : 30,
    invoicePrefix: clampLine(input["invoicePrefix"], 20),
  };
}

function formatDate(iso: string, format: ExportDateFormat): string {
  const [y = "", m = "", d = ""] = iso.split("-");
  if (format === "us") return `${m}/${d}/${y}`;
  if (format === "uk") return `${d}/${m}/${y}`;
  return iso;
}

const addDays = (iso: string, days: number): string => {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const money = (cents: number | null): string => (cents === null ? "" : (cents / 100).toFixed(2));

/**
 * One CSV cell, quoted when it has to be (RFC 4180), and defused when it would
 * otherwise be read as a formula. A client or item called `=HYPERLINK(...)`
 * opens in whatever spreadsheet the bookkeeper uses, so a text cell starting
 * with `= + - @` or a tab gets a leading apostrophe, the usual defence.
 * Numbers are formatted here and never start with one of those.
 */
export function csvCell(value: string, text = true): string {
  const safe = text && /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/**
 * The month-end export: one row per billing line, in the template's columns.
 *
 * `invoice_ref` is one number per client in this export (clients in name
 * order, numbered from 1, after the template's prefix and the export date),
 * which is how QuickBooks groups lines into invoices: every line that shares
 * an invoice number is one invoice. Pure, so the node suite tests it and the
 * browser builds the file without another route.
 */
export function buildBillingCsv(
  lines: readonly BillingLine[],
  template: ExportTemplateSpec,
  exportedOn: string,
): string {
  const clients = [...new Set(lines.map((line) => line.clientName))].sort((a, b) => a.localeCompare(b));
  const stamp = exportedOn.replace(/-/g, "");
  const refFor = (client: string): string =>
    `${template.invoicePrefix}${stamp}-${String(clients.indexOf(client) + 1).padStart(2, "0")}`;

  const cell = (line: BillingLine, column: ExportColumn): string => {
    switch (column.source) {
      case "deployed_on":
        return csvCell(formatDate(line.deployedOn, template.dateFormat), false);
      case "client":
        return csvCell(line.clientName);
      case "title":
        return csvCell(line.title);
      case "description":
        return csvCell(line.description);
      case "serial":
        return csvCell(line.serial);
      case "category":
        return csvCell(line.categoryName);
      case "quantity":
        return String(line.quantity);
      case "unit_price":
        return money(line.unitPriceCents);
      case "line_total":
        return money(line.unitPriceCents * line.quantity);
      case "unit_cost":
        return money(line.unitCostCents);
      case "line_cost":
        return money(line.unitCostCents === null ? null : line.unitCostCents * line.quantity);
      case "vendor":
        return csvCell(line.vendor);
      case "vendor_invoice":
        return csvCell(line.vendorInvoice);
      case "invoice_ref":
        return csvCell(refFor(line.clientName));
      case "invoice_date":
        return csvCell(formatDate(exportedOn, template.dateFormat), false);
      case "due_date":
        return csvCell(formatDate(addDays(exportedOn, template.dueDays), template.dateFormat), false);
      case "deployment_id":
        return csvCell(line.kind === "shipping" ? `${line.deploymentId}-ship` : line.deploymentId);
      case "constant":
        return csvCell(column.value ?? "");
    }
  };

  const sorted = [...lines].sort(
    (a, b) =>
      a.clientName.localeCompare(b.clientName) ||
      a.deployedOn.localeCompare(b.deployedOn) ||
      a.deploymentId.localeCompare(b.deploymentId) ||
      a.kind.localeCompare(b.kind),
  );
  const rows = [
    template.columns.map((column) => csvCell(column.header)).join(","),
    ...sorted.map((line) => template.columns.map((column) => cell(line, column)).join(",")),
  ];
  // CRLF, the line ending RFC 4180 names and every accounting import accepts.
  return `${rows.join("\r\n")}\r\n`;
}

export const billingFileName = (templateName: string, exportedOn: string): string =>
  `utucat-billing-${
    templateName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "export"
  }-${exportedOn}.csv`;

/* ------------------------------------------------ the stock export (5) -- */

/**
 * Where a receipt's invoice or a retirement's certificate lands in the export
 * zip. The attachment's id keeps two files called `invoice.pdf` apart; the
 * name is sanitised because it came from somebody's disk.
 */
export const stockAttachmentPath = (attachment: AttachmentMeta): string =>
  `stock/attachments/${attachment.id}-${attachment.name.replace(/[^\w.-]+/g, "_").slice(0, 80) || "file"}`;

const csv = (header: string[], rows: (string | number | null)[][]): string =>
  `${[header, ...rows]
    .map((row) =>
      row.map((value) => (typeof value === "number" ? String(value) : csvCell(value ?? ""))).join(","),
    )
    .join("\r\n")}\r\n`;

/**
 * A stockroom's records as one CSV per table, for the Stage 5 export: CSVs
 * rather than more JSON because the people who want these open them in a
 * spreadsheet. Every row carries its own id and the ids it points at, so the
 * files join back together; the item's title and serial ride along on each
 * row so none of them has to be joined to be read.
 *
 * Cost and the vendor's invoice number are in here on purpose: this is the
 * owner's own copy of their own books, the same reasoning that lets the
 * export carry private fields (docs/notes/invariants.md).
 */
export function buildStockCsvs(records: StockRecords): { name: string; text: string }[] {
  const items = new Map(records.stock.map((row) => [row.itemId, row]));
  const clients = new Map(records.clients.map((client) => [client.id, client.name]));
  const title = (itemId: string) => items.get(itemId)?.title ?? "";
  const serial = (itemId: string) => items.get(itemId)?.serial ?? "";

  return [
    {
      name: "stock/stock.csv",
      text: csv(
        [
          "item_id",
          "title",
          "serial",
          "tracking",
          "state",
          "on_hand",
          "deployed",
          "unit_cost",
          "receipt_id",
          "client",
        ],
        records.stock.map((row) => [
          row.itemId,
          row.title,
          row.serial,
          row.tracking,
          row.state,
          row.onHand,
          row.deployed,
          money(row.unitCostCents),
          row.receiptId,
          row.clientId ? (clients.get(row.clientId) ?? "") : "",
        ]),
      ),
    },
    {
      name: "stock/receipts.csv",
      text: csv(
        [
          "receipt_id",
          "received_on",
          "vendor",
          "invoice_number",
          "items",
          "total_cost",
          "notes",
          "file",
          "entered_by",
        ],
        records.receipts.map((receipt) => [
          receipt.id,
          receipt.receivedOn,
          receipt.vendor,
          receipt.invoiceNumber,
          receipt.itemCount,
          money(receipt.totalCostCents),
          receipt.notes,
          receipt.attachment ? stockAttachmentPath(receipt.attachment) : "",
          receipt.actor,
        ]),
      ),
    },
    {
      name: "stock/clients.csv",
      text: csv(
        ["client_id", "name", "colour", "units_out_now"],
        records.clients.map((client) => [client.id, client.name, client.color ?? "", client.deployedUnits]),
      ),
    },
    {
      name: "stock/deployments.csv",
      text: csv(
        [
          "deployment_id",
          "deployed_on",
          "client",
          "where_at_client",
          "item_id",
          "title",
          "serial",
          "quantity",
          "returned",
          "unit_cost",
          "unit_price",
          "shipping",
          "billable",
          "billed_on",
          "entered_by",
        ],
        records.deployments.map((line) => [
          line.id,
          line.deployedOn,
          clients.get(line.clientId) ?? "",
          line.spot,
          line.itemId,
          title(line.itemId),
          serial(line.itemId),
          line.quantity,
          line.returned,
          money(line.unitCostCents),
          money(line.unitPriceCents),
          money(line.shippingCents),
          line.billable ? "yes" : "no",
          line.billedAt ? new Date(line.billedAt * 1000).toISOString().slice(0, 10) : "",
          line.actor,
        ]),
      ),
    },
    {
      name: "stock/retirements.csv",
      text: csv(
        [
          "retirement_id",
          "retired_on",
          "item_id",
          "title",
          "serial",
          "quantity",
          "reason",
          "disposal_vendor",
          "note",
          "certificate",
          "entered_by",
        ],
        records.retirements.map((row) => [
          row.id,
          row.retiredOn,
          row.itemId,
          title(row.itemId),
          serial(row.itemId),
          row.quantity,
          RETIRE_REASON_LABELS[row.reason],
          row.disposalVendor,
          row.note,
          row.certificate ? stockAttachmentPath(row.certificate) : "",
          row.actor,
        ]),
      ),
    },
    {
      name: "stock/movements.csv",
      text: csv(
        [
          "occurred_on",
          "what",
          "item_id",
          "title",
          "serial",
          "quantity",
          "where",
          "price",
          "cost",
          "note",
          "who",
          "receipt_id",
          "deployment_id",
          "retirement_id",
        ],
        records.movements.map((movement) => [
          movement.occurredOn,
          MOVEMENT_LABELS[movement.kind],
          movement.itemId,
          title(movement.itemId),
          serial(movement.itemId),
          movement.quantity,
          movement.clientName || "Stockroom",
          money(movement.priceCents),
          money(movement.costCents),
          movement.note,
          movement.actor,
          movement.receiptId,
          movement.deploymentId,
          movement.retirementId,
        ]),
      ),
    },
    {
      name: "stock/reserves.csv",
      text: csv(
        [
          "reserve_id",
          "client",
          "tags",
          "minimum",
          "target",
          "counted_now",
          "paid_in_advance",
          "credit_units",
          "quiet_until",
          "quiet_reason",
          "note",
        ],
        records.reserves.map((reserve) => [
          reserve.id,
          reserve.clientId ? reserve.clientName : "General pool",
          reserve.tags.join("; "),
          reserve.minimum,
          reserve.target,
          reserve.count,
          reserve.prepaid ? "yes" : "no",
          reserve.credit,
          reserve.quietUntil ?? "",
          reserve.quietReason ?? "",
          reserve.note,
        ]),
      ),
    },
    {
      name: "stock/holds.csv",
      text: csv(
        [
          "hold_id",
          "held_on",
          "reserve_id",
          "item_id",
          "title",
          "serial",
          "held_now",
          "billing",
          "unit_price",
          "billed_on",
          "entered_by",
        ],
        records.holds.map((hold) => [
          hold.id,
          hold.heldOn,
          hold.reserveId,
          hold.itemId,
          hold.title,
          hold.serial,
          hold.open,
          hold.billing,
          money(hold.unitPriceCents),
          hold.billedAt ? new Date(hold.billedAt * 1000).toISOString().slice(0, 10) : "",
          hold.actor,
        ]),
      ),
    },
  ];
}

/**
 * Units received one per serial that are really a count (eight speakers
 * nobody tracks by serial), merged into one counted line. The first id is the
 * item that stays; the others' history, photos and tags move onto it, and
 * their serials are kept in its notes. `POST /api/stock/merge`.
 */
export interface MergeUnitsRequest {
  itemIds: string[];
  on: string;
}

/**
 * A counted line that is really serialized, split into one item per unit,
 * each copying the line's details. `serials` go on the units in order; fewer
 * than the count leaves the rest to fill in later. `POST /api/stock/split`.
 */
export interface SplitLineRequest {
  itemId: string;
  serials: string[];
  on: string;
}

/** What a merge or a split answers with: the items it leaves, the kept or first one first. */
export interface ConvertResponse {
  itemIds: string[];
}

/** Units one merge takes at most: a bound on a mistake, like the receipt caps. */
export const MAX_MERGE_UNITS = MAX_RECEIPT_UNITS;
